import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';
import toast from 'react-hot-toast';
import { loginRequest } from '../authConfig';

const LoginPage = () => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if user is already logged in (including after redirect)
    const checkAndRedirect = async () => {
      try {
        const currentAccounts = instance.getAllAccounts();
        if (currentAccounts.length > 0) {
          console.log('User already logged in, redirecting to dashboard');
          navigate('/dashboard');
        } else {
          // Try to handle any pending redirect
          const response = await instance.handleRedirectPromise();
          if (response && response.account) {
            console.log('Login redirect processed:', response.account);
            navigate('/dashboard');
          }
        }
      } catch (error) {
        console.error('Error checking login status:', error);
      }
    };

    checkAndRedirect();
  }, [instance, navigate]);

  const handleLogin = async () => {
    setLoading(true);
    try {
      // Try redirect login instead of popup (more reliable)
      await instance.loginRedirect(loginRequest);
      // After redirect, user will come back to the app automatically
    } catch (error) {
      console.error(error);
      if (error.errorCode === 'user_cancelled') {
        toast.error('Login cancelled');
      } else {
        toast.error('Login failed. Please try again.');
      }
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#000000',
        padding: 2,
      }}
    >
      <Box
        sx={{
          backgroundColor: '#ffffff',
          borderRadius: 2,
          padding: { xs: 4, sm: 6, md: 8 },
          width: '100%',
          maxWidth: 480,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          textAlign: 'center',
        }}
      >
        {/* Logo */}
        <Box sx={{ mb: 6 }}>
          <img
            src="/acom-logo.png"
            alt="ACOM Logo"
            style={{
              width: '280px',
              maxWidth: '100%',
              height: 'auto',
            }}
          />
        </Box>

        {/* Title */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '24px', sm: '32px' },
            mb: 3,
            color: '#000000',
          }}
        >
          Apps Portal
        </Typography>

        {/* Subtitle */}
        <Typography
          variant="body1"
          sx={{
            fontSize: '18px',
            mb: 5,
            color: '#333333',
          }}
        >
          Please sign in with your ACOM account
        </Typography>

        {/* Sign in button */}
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleLogin}
          disabled={loading}
          sx={{
            backgroundColor: '#0078d4',
            color: '#ffffff',
            fontSize: '18px',
            fontWeight: 500,
            padding: '12px 24px',
            borderRadius: '4px',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#106ebe',
            },
            '&:disabled': {
              backgroundColor: '#cccccc',
            },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} color="inherit" />
              Signing in...
            </>
          ) : (
            <>
              <Box
                component="svg"
                sx={{ width: 21, height: 21 }}
                viewBox="0 0 21 21"
                fill="none"
              >
                <rect width="10" height="10" fill="#F25022" />
                <rect x="11" width="10" height="10" fill="#7FBA00" />
                <rect y="11" width="10" height="10" fill="#00A4EF" />
                <rect x="11" y="11" width="10" height="10" fill="#FFB900" />
              </Box>
              Sign in with Microsoft
            </>
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default LoginPage;